/**
 * Route copy for `src/routes/ads` (keys: `routes.ads.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  adBillingPanel: {
    active: 'Active',
    disabled: 'Disabled',
    unpaid: 'Unpaid',
    underReview: 'Under review',
    gracePeriod: 'Grace period',
    loadingBilling: 'Loading billing…',
    billingUnavailableAdAccountNotConnected: 'Billing unavailable — ad account not connected or billing scope not granted by the platform.',
    tryAgain: 'Try again',
    accountLabel: 'Account: {{label}}',
    spentToday: 'Spent today',
    thisMonth: 'This month',
    totalSpent: 'Total spent',
    prepaidBalance: 'Prepaid balance',
    accountSpendingCap: 'Account spending cap',
    fundingSource: 'Funding source:',
    notProvided: 'Not provided',
    recentTransactions: 'Recent transactions',
    date: 'Date',
    type: 'Type',
    status: 'Status',
    amount: 'Amount',
  },
  adEngagementPanel: {
    reactions: 'Reactions',
    comments: 'Comments',
    shares: 'Shares',
    saves: 'Saves',
    videoViews: 'Video views',
    linkClicks: 'Link clicks',
    loadingEngagement: 'Loading engagement…',
    engagementUnavailableAdAccountNotConnect: 'Engagement unavailable — ad account not connected or ad never delivered.',
    tryAgain: 'Try again',
    refreshing: 'Refreshing…',
    refreshLive: 'Refresh live',
    noEngagementDataMeasuredYet: 'No engagement data measured yet.',
    likes: 'Likes',
    video: 'Video',
    views: 'Views',
    avgDuration: 'Avg. duration',
    deliveryRankingsMeta: 'Delivery rankings (Meta)',
    quality: 'Quality',
    engagement: 'Engagement',
    conversion: 'Conversion',
    rankingRelativeToTheAdsCompeting: 'Ranking relative to the ads competing for the same audience. “Below average” means a creative to rework.',
    vTrendOverLengthDays: '{{v}} trend over {{length}} days',
    dailyReadingsLatest: 'daily readings · latest:',
    reactionsByType: 'Reactions by type',
    openThisAdSCommentsIn: 'Open this ad\'s comments in the inbox →',
    commentsOnSponsoredPostsAreIngested: 'Comments on sponsored posts are ingested into the unified inbox (marked “ad”) — reply to them like any other message.',
  },
  adModals: {
    accountVLinked: 'Account {{v}} linked.',
    linkAnAdAccount: 'Link an ad account',
    cancel: 'Cancel',
    connecting: 'Connecting…',
    linkTheAccount: 'Link the account',
    chooseTheNetworkThenSelectThe: 'Choose the network, then select the connected ad account (OAuth) holding the token',
    adsManagement: 'ads_management',
    noConnectedAccountYetStartThe: '. No connected account yet? Start the secure one-click connection — we never store your credentials.',
    loadingConnectedAccounts: 'Loading connected accounts…',
    connectedAdAccount: 'Connected ad account',
    adAccountIdentifier: 'Ad account identifier',
    act123456789Or123456789: 'act_123456789 or 123456789',
    prefilledFromTheConnectionAdjustIt: 'Prefilled from the connection — adjust it if you manage several accounts.',
    theMetaAdsAccountIdentifierAct: 'The Meta Ads account identifier (act_…).',
    theGoogleAdsCustomerNumber10: 'The Google Ads customer number (10 digits).',
    nameOpt: 'Name (opt.)',
    mainAccount: 'Main account',
    currency: 'Currency',
    usd: 'USD',
    redirecting: 'Redirecting…',
    connectAnotherAdAccount: '+ Connect another ad account',
    noVAccountConnected: 'No {{v}} account connected',
    couldNotLoadTheConnectedAccounts: 'Could not load the connected accounts. Try again, or start a secure connection.',
    connectYourVAccountViaOauth: 'Connect your {{v}} account via OAuth to run your paid campaigns from this space.',
    connectAnAdAccount: 'Connect an ad account',
    retryLoading: 'Retry loading',
    removeV: 'Remove {{v}}',
    campaignUpdated: 'Campaign updated.',
    campaignCreated: 'Campaign created.',
    draftSavedClickPublishOnceAn: 'Draft saved — click “Publish” once an ad account is connected.',
    settings: 'Settings',
    creative: 'Creative',
    audience: 'Audience',
    editTheCampaign: 'Edit the campaign',
    newCampaign: 'New campaign',
    saving: 'Saving…',
    save: 'Save',
    createTheCampaign: 'Create the campaign',
    adAccount: 'Ad account',
    campaignName: 'Campaign name',
    summerPromo2026: 'Summer promo 2026',
    objective: 'Objective',
    budget: 'Budget',
    budgetType: 'Budget type',
    bidStrategy: 'Bid strategy',
    automatic: 'Automatic',
    startOpt: 'Start (opt.)',
    endOpt: 'End (opt.)',
    format: 'Format',
    facebookPage: 'Facebook page',
    adHeadline: 'Ad headline',
    discoverOurOffer: 'Discover our offer',
    primaryText: 'Primary text',
    engagingAdMessage: 'Engaging ad message…',
    videoUrl: 'Video URL',
    imageUrl: 'Image URL',
    mediaLibraryOrLink: '(media library or link)',
    mediaUrl: 'Media URL',
    carouselCards: 'Carousel cards',
    cardV: 'Card {{v}}',
    removeCardV: 'Remove card {{v}}',
    remove: 'Remove',
    mediaUrl2: 'Media URL',
    cardVMedia: 'Card {{v}} media',
    title: 'Title',
    cardVTitle: 'Card {{v}} title',
    destinationUrl: 'Destination URL',
    cardVLink: 'Card {{v}} link',
    addACard: '+ Add a card',
    callToAction: 'Call to action',
    none: 'None',
    preview: 'Preview',
    media: 'media',
    indicativePreviewTheFinalRenderingDepend: 'Indicative preview — the final rendering depends on the network and placement.',
    leaveEmptyBroadAudienceEachCriterion: 'Leave empty = broad audience. Each criterion is applied to the network at publishing time (Meta targeting spec / Google criteria).',
    countriesIsoCodesEGCa: 'Countries (ISO codes, e.g. CA, FR)',
    ca: 'CA',
    cities: 'Cities',
    montreal: 'Montreal',
    minAge: 'Min age',
    minimumAge: 'Minimum age',
    maxAge: 'Max age',
    maximumAge: 'Maximum age',
    gender: 'Gender',
    men: 'Men',
    women: 'Women',
    interests: 'Interests',
    fitness: 'fitness',
    keywords: 'Keywords',
    running: 'running',
    placements: 'Placements',
    customAudiences: 'Custom audiences',
    lookalikeAudiences: 'Lookalike audiences',
    excludedAudiences: 'Excluded audiences',
    postBoosted: 'Post boosted.',
    boostAPost: 'Boost a post',
    boosting: 'Boosting…',
    boost: 'Boost',
    turnAnExistingOrganicPostInto: 'Turn an existing organic post into a paid campaign. Enter its identifier (the page\'s object story id), the total budget and the duration.',
    postIdentifier: 'Post identifier',
    totalBudget: 'Total budget',
    durationDays: 'Duration (days)',
  },
  adReactionsBreakdown: {
    loadingReactions: 'Loading reactions…',
    reactionDetailsUnavailableAccountNotConn: 'Reaction details unavailable (account not connected or not supported).',
    noReactionsYet: 'No reactions yet.',
    total: 'Total',
    reactionsAggregatedAccountsOnlyNoPersona: 'reactions · aggregated accounts only (no personal data).',
  },
  adsBreakdownDashboard: {
    breakdownsUnavailable: 'Breakdowns unavailable.',
    breakdownsV: 'Breakdowns — {{v}}…',
    breakdowns: 'Breakdowns',
    campaignBreakdowns: 'Campaign breakdowns',
    backToAds: '← Back to ads',
    loading: 'Loading…',
    refresh: 'Refresh',
    level: 'Level',
    dimension: 'Dimension',
    metric: 'Metric',
    from: 'From',
    to: 'To',
    loadingBreakdowns: 'Loading breakdowns…',
    anErrorOccurred: 'An error occurred.',
    breakdownsComingSoon: 'Breakdowns coming soon',
    theAdsServiceDoesNotExpose: 'The ads service does not expose detailed breakdowns on this environment yet.',
    connectAnAdAccountAndPublish: 'Connect an ad account and publish the campaign to collect the breakdowns by {{v}}.',
    noDataMeasured: 'No data measured',
    noVReportedByThePlatform: 'No {{v}} reported by the platform for this period.',
    daily: '— daily',
    noDailySeries: 'No daily series',
    thePlatformDidNotReportA: 'The platform did not report a daily value for this metric.',
    byV: 'by {{v}}',
    noBreakdown: 'No breakdown',
    noVValueByV2: 'No {{v}} value by {{v2}}.',
    detailByVLength: 'Detail by {{v}} ({{length}})',
  },
  adsCalendar: {
    delivered: 'Delivered',
    paused: 'Paused',
    draft: 'Draft',
    archived: 'Archived',
    deliveryAdjustedOnThePlatform: 'Delivery adjusted on the platform.',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    dailyBudget: 'Daily budget',
    weekends: 'Weekends',
    adCalendarComingSoonServiceNot: 'Ad calendar coming soon (service not deployed or account not connected).',
    tryAgain: 'Try again',
    noAdDeliveryInThisPeriod: 'No ad delivery in this period.',
    cumulativeDailyBudgetCurrency: 'Cumulative daily budget ({{currency}})',
    dragTheEdgesOfAMeta: '· Drag the edges of a Meta bar to adjust the dates',
    editName: 'Edit — {{name}}',
    thisCampaignIsADraftArchived: 'This campaign is a draft / archived — open it in the ads manager to edit it.',
  },
  adsCommandCenter: {
    adV: 'Ad {{v}}',
    engagement: 'Engagement',
    creativePreview: 'Creative preview',
    loadingThePreview: 'Loading the preview…',
    previewUnavailableAccountNotConnectedOr: 'Preview unavailable (account not connected or not supported).',
    couldNotLoadThePreview: 'Could not load the preview.',
    loadingAds: 'Loading ads…',
    adListUnavailableCampaignNotPublished: 'Ad list unavailable — campaign not published on the platform or account not connected.',
    tryAgain: 'Try again',
    noAdsForThisCampaign: 'No ads for this campaign.',
    loadingTookTooLongTheAds: 'Loading took too long. The ads service may be temporarily unavailable — try again.',
    adsControlCenter: 'Ads control center',
    loading: 'Loading…',
    theAdsServiceIsNotAvailable: 'The ads service is not available yet. Connect an ad account from the Ads page to enable the control center.',
    allTheDetailsTrackingAndEditing: 'All the details, tracking and editing of your ads — without leaving Karexia.',
    numberOfCampaignsCurrentlyDelivering: 'Number of campaigns currently delivering',
    livecountDelivering: '{{liveCount}} delivering',
    noActiveDelivery: 'No active delivery',
    account: 'Account',
    campaign: 'Campaign',
    controlCenterSections: 'Control center sections',
    adsEngagement: 'Ads & engagement',
    billing: 'Billing',
    calendar: 'Calendar',
    aiPlanner: 'AI planner',
    noCampaignsCreateOneFromThe: 'No campaigns. Create one from the Ads page.',
    noAdAccountConnected: 'No ad account connected.',
  },
  adsOptimization: {
    decidedOn: ' on {{date}}',
    adAccountNotConnectedMetricsUnavailable: 'Ad account not connected: metrics unavailable.',
    theImpressionThresholdMustBeA: 'The impression threshold must be a positive number.',
    aBTestStarted: 'A/B test started.',
    forceTheWinner: 'Force the winner',
    promoteTheWinner: 'Promote the winner',
    pauseEveryOtherAdAndKeep: 'Pause every other ad and keep this one, without waiting for the statistical threshold?',
    pauseTheLosingAdsAndKeep: 'Pause the losing ads and keep the winner? Nothing is activated automatically.',
    force: 'Force',
    promote: 'Promote',
    winnerKeptLengthAdSPaused: 'Winner kept; {{length}} ad(s) paused.',
    theABTestIsAvailable: 'The A/B test is available once the campaign is published on the platform (at least two active ads).',
    loadingTheABTest: 'Loading the A/B test…',
    tryAgain: 'Try again',
    noABTestCompareYour: 'No A/B test. Compare your ads (creatives) on CTR or conversion rate; the losers will be paused once the impression threshold is reached.',
    atLeastTwoPublishedCreativesAre: 'At least two published creatives are needed in this campaign (currently {{creativeCount}}).',
    metric: 'Metric',
    ctrClickThroughRate: 'CTR (click-through rate)',
    conversionRate: 'Conversion rate',
    minImpressionsAd: 'Min. impressions / ad',
    starting: 'Starting…',
    startTheABTest: 'Start the A/B test',
    decided: 'Decided',
    inProgress: 'In progress',
    metric2: 'Metric:',
    cachedData: '· cached data (',
    refreshUnavailable: 'refresh unavailable',
    ad: 'Ad',
    impr: 'Impr.',
    clicks: 'Clicks',
    conv: 'Conv.',
    cvr: 'CVR',
    kept: 'Kept',
    winner: 'Winner',
    keepThisAdAndPauseThe: 'Keep this ad and pause the others (without waiting for the threshold)',
    notEnoughDataYet: 'Not enough data yet',
    theWinnerIsDesignatedWhenEach: '— the winner is designated when each ad reaches the impression threshold.',
    testDecided: 'Test decided',
    theOtherAdsHaveBeenPaused: ': the other ads have been paused.',
    availableWhenTheStatisticalThresholdIs: 'Available when the statistical threshold is reached',
    processing: 'Processing…',
    refresh: 'Refresh',
    frequencyMaxFrequency: 'frequency > {{max_frequency}}',
    antiFatigueGuard: 'Anti-fatigue guard',
    thresholdsMustBePositiveNumbers: 'Thresholds must be positive numbers.',
    enterAtLeastOneConditionFrequency: 'Enter at least one condition (frequency, CTR drop or CPA rise).',
    theImpressionMinimumIsInvalid: 'The impression minimum is invalid.',
    ruleAdded: 'Rule added.',
    deleteTheRule: 'Delete the rule',
    deleteTheRuleName: 'Delete the rule “{{name}}”?',
    delete: 'Delete',
    ruleDeleted: 'Rule deleted.',
    loadingRules: 'Loading rules…',
    noRulesARuleWatchesThe: 'No rules. A rule watches the campaign (frequency, CTR drop, CPA rise) and pauses it or notifies you.',
    disabled: '(disabled)',
    minVImpr: '· min. {{v}} impr. ·',
    triggeredOnV: ' · triggered on {{v}}',
    deleteName: 'Delete {{name}}',
    newRule: 'New rule',
    name: 'Name',
    maxFrequency: 'Max frequency',
    eG35: 'e.g. 3.5',
    ctrDrop: 'CTR drop (%)',
    eG30: 'e.g. 30',
    cpaRise: 'CPA rise (%)',
    eG50: 'e.g. 50',
    minImpressions: 'Min. impressions',
    action: 'Action',
    adding: 'Adding…',
    addTheRule: 'Add the rule',
    theRuleFiresAsSoonAs: 'The rule fires as soon as ONE of the filled-in conditions is exceeded.',
    noPixelFoundOnThisAccount: 'No pixel found on this account: enter its identifier manually.',
    accountNotConnectedPixelDiscoveryUnavail: 'Account not connected: pixel discovery unavailable. Enter the identifier manually.',
    discoveryNotPossibleVEnterThe: 'Discovery not possible ({{v}}). Enter the identifier manually.',
    pixelRemoved: 'Pixel removed.',
    pixelSaved: 'Pixel saved.',
    pixelV: 'Pixel — {{v}}',
    removeThePixel: 'Remove the pixel',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    thePixelDatasetIsOnlySupported: 'The pixel / dataset is only supported for Meta accounts.',
    onceThePixelIsSetNew: 'Once the pixel is set, new Meta “Sales” / “Leads” campaigns optimize for on-site conversions. Existing campaigns are not changed.',
    discoveringPixels: 'Discovering pixels…',
    accountPixels: 'Account pixels',
    chooseAPixel: '— Choose a pixel —',
    pixelId: 'Pixel ID',
    datasetIdCapi: 'Dataset ID (CAPI)',
    optional: 'optional',
    identifiersOnlyContainDigits: 'Identifiers only contain digits.',
    conversionsApiCapiServerEventsSent: 'Conversions API (CAPI): server events sent only with the contact\'s consent (CASL / Law 25).',
    conversionsApiCapiDisabledSetA: 'Conversions API (CAPI) disabled: set a pixel or a dataset to enable it.',
    audienceCreated: 'Audience created.',
    adAccountNotConnectedCannotCreate: 'Ad account not connected: cannot create the audience.',
    enterAtLeastOneValidEmail: 'Enter at least one valid email or phone number.',
    submittedContactSSentHashedV: '{{submitted}} contact(s) sent (hashed){{v}}.',
    nameCountry2LettersAndSize: 'Name, country (2 letters) and size (1 to 20%) required.',
    lookalikeAudienceCreated: 'Lookalike audience created.',
    audiences: 'Audiences',
    close: 'Close',
    customLookalikeAudiencesRequireALinked: 'Custom / lookalike audiences require a linked Meta account.',
    adAccount: 'Ad account',
    newCustomAudience: 'New custom audience',
    nameEG2026Customers: 'Name (e.g. 2026 customers)',
    descriptionOptional: 'Description (optional)',
    createTheAudience: 'Create the audience',
    loading: 'Loading…',
    noAudienceForThisAccount: 'No audience for this account.',
    lookalike: 'Lookalike',
    custom: 'Custom',
    countryVV2: 'Country {{v}} · {{v2}}',
    vMemberS: '{{v}} member(s)',
    addContacts: 'Add contacts',
    createALookalike: 'Create a lookalike',
    oneEmailOrPhonePerLine: 'One email or phone per line (or comma-separated)',
    valuesAreNormalizedAndHashedSha: 'Values are normalized and hashed (SHA-256) on the server; never stored in clear. Only import contacts who consented (CASL / Law 25).',
    send: 'Send',
    lookalikeAudienceName: 'Lookalike audience name',
    countryCa: 'Country (CA)',
    size: 'Size',
    createTheLookalikeAudience: 'Create the lookalike audience',
    removeV: 'Remove {{v}}',
    noAudienceAvailable: 'No audience available',
    chooseAnAudience: '+ Choose an audience…',
    listUnavailableUnavailableUseManualEntry: 'List unavailable ({{unavailable}}) — use manual entry.',
    orEnterAnIdManually: 'or enter an ID manually…',
  },
  adsPage: {
    noConversionReturnedByThePlatform: 'No conversion returned by the platform. Conversion tracking requires a conversion event / pixel configured on the ad account.',
    adAccount: 'Ad account',
    campaignName: 'Campaign name',
    objective: 'Objective',
    budget: 'Budget',
    budgetType: 'Budget type',
    bidStrategy: 'Bid strategy',
    startOpt: 'Start (opt.)',
    endOpt: 'End (opt.)',
    unlinkTheAccountLabelAndIts: 'Unlink the account “{{label}}” and its campaigns?',
    accountUnlinked: 'Account unlinked.',
    archiveTheCampaign: 'Archive the campaign',
    archiveNameAnArchivedCampaignCan: 'Archive “{{name}}”? An archived campaign can no longer be reactivated.',
    archive: 'Archive',
    nameStatusV: '“{{name}}” — status: {{v}}.',
    publishTheCampaign: 'Publish the campaign',
    publishNameOnThePlatformThe: 'Publish “{{name}}” on the platform? The campaign (targeting, creative, schedule and budget) will be created paused — you can activate it afterwards.',
    publish: 'Publish',
    namePublishedStatusV: '“{{name}}” published — status: {{v}}.',
    noAdAccountConnectedCannotPublish: 'No ad account connected: cannot publish. The draft is kept.',
    deleteTheCampaign: 'Delete the campaign',
    permanentlyDeleteNameThisCannotBe: 'Permanently delete “{{name}}”? This cannot be undone.',
    delete: 'Delete',
    campaignDeleted: 'Campaign deleted.',
    ads: 'Ads',
    manageYourPaidCampaignsAndBoost: 'Manage your paid campaigns and boost your organic posts — Meta, Google, TikTok, LinkedIn.',
    refresh: 'Refresh',
    createWithAi: '✨ Create with AI',
    audiences: 'Audiences',
    boostAPost: 'Boost a post',
    newCampaign: 'New campaign',
    linkAnAccount: 'Link an account',
    analysisRecommendationsShouldYouBoost: 'Analysis & recommendations — should you boost?',
    loadingCampaigns: 'Loading campaigns…',
    adManagementIsNotAvailableOn: 'Ad management is not available on this environment yet (service being deployed). Try again later.',
    viewThePlans: 'View the plans',
    couldNotLoadTheAds: 'Could not load the ads.',
    tryAgain: 'Try again',
    connectAnAdAccount: 'Connect an ad account',
    linkAMetaGoogleTiktokOr: 'Link a Meta, Google, TikTok or LinkedIn Ads account to run your paid campaigns, track spend and boost your best posts — all from this space.',
    linkAnAdAccount: 'Link an ad account',
    noAdAccountConnectedContactAn: 'No ad account connected. Contact an administrator to link one.',
    linkedAccounts: 'Linked accounts',
    pixelPixelId: 'Pixel {{pixel_id}}',
    noPixelSet: 'No pixel set',
    pixel: 'Pixel ✓',
    pixel2: 'Pixel',
    unlinkTheAccountLabel: 'Unlink the account {{label}}',
    unlinkThisAccount: 'Unlink this account',
    clickRateCtrPercent: 'Click rate {{ctr}} percent',
    conversionRateCvrPercent: 'Conversion rate {{cvr}} percent',
    conversionRateUnavailableNoConversionEve: 'Conversion rate unavailable — no conversion event configured',
    spend: 'Spend',
    impressions: 'Impressions',
    clicks: 'Clicks',
    conversions: 'Conversions',
    spendPerCampaign: 'Spend per campaign',
    spendBreakdownByCampaign: 'Spend breakdown by campaign',
    activeCampaigns: 'Active campaigns',
    bestCtrNameCtr: 'Best CTR: {{name}} ({{ctr}}%).',
    lowCtrNameCtrToOptimize: 'Low CTR: {{name}} ({{ctr}}%) — to optimize.',
    largestBudgetNameV: 'Largest budget: {{name}} ({{v}}).',
    noActiveCampaignRightNow: 'No active campaign right now.',
    campaignBreakdown: 'Campaign breakdown',
    keyTakeaway: 'Key takeaway',
    edit: 'Edit',
    publishTheCampaignOnThePlatform: 'Publish the campaign on the platform',
    publishName: 'Publish — {{name}}',
    deleteName: 'Delete — {{name}}',
    campaign: 'Campaign',
    boostedPost: 'Boosted post',
    boost: 'Boost',
    status: 'Status',
    review: 'Review',
    network: 'Network',
    actions: 'Actions',
    campaignsLength: 'Campaigns ({{length}})',
    searchForACampaign: 'Search for a campaign…',
    noCampaignsYetCreateOneOr: 'No campaigns yet. Create one or boost a post.',
    loAndOlder: '{{lo}} and older',
    upToHiYearsOld: 'up to {{hi}} years old',
    regions: 'Regions',
    age: 'Age',
    interests: 'Interests',
    keywords: 'Keywords',
    placements: 'Placements',
    customAudiences: 'Custom audiences',
    lookalikeAudiences: 'Lookalike audiences',
    excludedAudiences: 'Excluded audiences',
    creativeV: 'Creative {{v}}',
    mediaMediaUrl: 'Media: {{media_url}}',
    lengthCarouselCardS: '{{length}} carousel card(s)',
    viewTheBreakdowns: 'View the breakdowns',
    editTargetingBudgetEnd: 'Edit targeting / budget / end',
    editTheCampaign: 'Edit the campaign',
    analysisRecommendationsForThisCampaign: 'Analysis & recommendations for this campaign',
    livePerformance: 'Live performance',
    configuration: 'Configuration',
    currency: 'Currency',
    createdOn: 'Created on',
    schedule: 'Schedule',
    start: 'Start',
    end: 'End',
    liveReadNotPossibleLiveRefresh: 'Live read not possible ({{live_refresh_error}}) — saved values shown.',
    targetingBudgetAndScheduleReadLive: 'Targeting, budget and schedule read live from the platform{{v}}.',
    editTheCampaignLive: 'Edit the campaign live',
    targeting: 'Targeting',
    broadTargetingNoSpecificCriteria: 'Broad targeting (no specific criteria).',
    platformReview: 'Platform review',
    thePlatformReportsAProblemWithout: 'The platform reports a problem without giving the reason.',
    aBTest: 'A/B test',
    antiFatigueRules: 'Anti-fatigue rules',
    creativesLength: 'Creatives ({{length}})',
    noCreativeAttachedToThisCampaign: 'No creative attached to this campaign.',
    loadingStatistics: 'Loading statistics…',
    noDataYetThePlatformHas: 'No data yet: the platform has not returned any statistics for this campaign yet.',
    noDataYetThisCampaignHas: 'No data yet: this campaign has not been published on the platform yet (local draft).',
    noDataYetTheAdAccount: 'No data yet: the ad account is not connected — cannot retrieve the statistics.',
    scope: 'Scope',
    convRate: 'Conv. rate',
    enable: 'Enable',
    country: 'Country',
    cities: 'Cities',
    languages: 'Languages',
    pause: 'Pause',
  },
  adsPlannerPanel: {
    campaignDraftCreatedNotLaunched: 'Campaign draft created (not launched).',
    confidenceV: 'Confidence {{v}}%',
    suggestedBudget: 'Suggested budget:',
    durationDurationDaysD: 'Duration: {{duration_days}} d',
    suggestedLaunch: 'Suggested launch:',
    audienceAudienceSummary: 'Audience: {{audience_summary}}',
    creativeCreativeHint: 'Creative: {{creative_hint}}',
    draftCreated: '✓ Draft created',
    creating: 'Creating…',
    acceptCreateADraft: 'Accept → create a draft',
    noProposalGeneratedForTheseCriteria: 'No proposal generated for these criteria.',
    thePlannerReadsYourSignalsRecommendation: 'The planner reads your signals (recommendations, best slots, content calendar, balance) and proposes campaigns.',
    itLaunchesNothing: 'It launches nothing',
    acceptingAProposalCreatesADraft: ': accepting a proposal creates a draft to review.',
    businessGoalYourWords: 'Business goal (your words)',
    eGIncreaseSignUpsFor: 'E.g. increase sign-ups for the November 15 event',
    campaignObjective: 'Campaign objective',
    letThePlannerChoose: 'Let the planner choose',
    indicativeBudget: 'Indicative budget (',
    optional: 'Optional',
    analyzing: 'Analyzing…',
    generateProposals: 'Generate proposals',
    readOnlyPlanningDisabled: 'Read-only — planning disabled.',
    plannerComingSoonServiceNotDeployed: 'Planner coming soon (service not deployed).',
    missingSignalsLessPreciseProposals: 'Missing signals (less precise proposals):',
  },
  coverage: {
    notAvailable: 'Not available',
  },
  creativeAdWizard: {
    mediaCount: '{{images}} image(s), {{videos}} video(s)',
    brief: 'Brief',
    generation: 'Generation',
    draft: 'Draft',
    metaFeedLink1911: 'Meta feed link 1.91:1',
    metaSquare11: 'Meta square 1:1',
    metaStory916: 'Meta story 9:16',
    metaReel916: 'Meta reel 9:16',
    googleResponsiveSearchAd: 'Google responsive search ad',
    done: 'Done',
    inProgress: 'In progress…',
    paused: 'Paused',
    failed: 'Failed',
    unavailable: 'Unavailable',
    skipped: 'Skipped',
    pending: 'Pending',
    connectionLostRetrying: 'Connection lost, retrying…',
    generationRestarted: 'Generation restarted.',
    ads: 'Ads',
    createAnAdWithAi: 'Create an ad with AI',
    pasteAProductUrlTheAi: 'Paste a product URL: the AI writes the copy, generates the image (and the video), then prepares a PAUSED campaign to review before publishing.',
    backToAds: '← Back to ads',
    steps: 'Steps',
    yourRoleIsReadOnlyYou: 'Your role is read-only: you cannot start a generation.',
    productUrl: 'Product URL',
    enterAFullUrlHttpOr: 'Enter a full URL (http:// or https://).',
    platform: 'Platform',
    adAccount: 'Ad account',
    loading: 'Loading…',
    noVAccount: 'No {{v}} account',
    objective: 'Objective',
    noVAccountLinkedLinkOne: 'No {{v}} account linked. Link one from the Ads page to generate a draft.',
    openAds: 'Open Ads',
    adFormatS: 'Ad format(s)',
    noFormatSelectedThePlatformS: 'No format selected = the platform\'s default format.',
    generateAVideo: 'Generate a video',
    addsAnAiGenerated916: 'Adds an AI-generated 9:16 reel (uses extra AI credits).',
    availableForMetaOnly: ' Available for Meta only.',
    theCampaignWillBeCreatedPaused: 'The campaign will be created paused — nothing is published without your approval.',
    launching: 'Launching…',
    generateTheAd: '✨ Generate the ad',
    generationFailed: 'Generation failed',
    generationPaused: 'Generation paused',
    generating: 'Generating…',
    generationComplete: 'Generation complete',
    aStepFailed: 'A step failed.',
    stepsAlreadyCompletedWillNotBe: 'Steps already completed will not be charged again on resume.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits: top up your wallet, then resume.',
    generationIsPausedSessionExpiredOr: 'Generation is paused (session expired or credits).',
    resuming: 'Resuming…',
    resumeTheGeneration: 'Resume the generation',
    editTheBrief: 'Edit the brief',
    pausedDraft: 'Paused · draft',
    yourCampaignIsReadyToReview: 'Your campaign is ready to review',
    generatedContentDraftNotCreated: 'Generated content — draft not created',
    product: 'Product',
    account: 'Account',
    copyVariants: 'Copy variants',
    media: 'Media',
    defaultValuesAppliedAdjustBeforePublishi: 'Default values applied — adjust before publishing',
    reviewInAds: 'Review in Ads →',
    createAnotherAd: 'Create another ad',
    findItInTheCampaignList: '— find it in the campaign list (status “Draft” / “Paused”), edit it, then publish it.',
    defaultBudgetAppliedPerDayChange: 'Default budget applied (per day) — change it before publishing.',
    defaultAudienceCanadaCaDefineYour: 'Default audience: Canada (CA) — define your audience before publishing.',
    productDetected: 'Product detected',
    copyVariantsLength: 'Copy variants ({{length}})',
    generatedMediaLength: 'Generated media ({{length}})',
    generatedVisual: 'Generated visual',
  },
  creativePreview: {
    feedMobileSimple: 'Feed · mobile (simple)',
    mobileBanner: 'Mobile banner',
    rightColumn: 'Right column',
    instantArticle: 'Instant article',
    preview: 'Preview',
    noPreviewReturnedByThePlatform: 'No preview returned by the platform for this ad.',
    previewFormats: 'Preview formats',
    adPreviewV: 'Ad preview — {{v}}',
    noPreviewForThisFormat: 'No preview for this format.',
    previewRenderedByThePlatformIn: 'Preview rendered by the platform in an isolated frame (sandbox) — no script accesses Karexia.',
    previewUnavailable: 'Preview unavailable.',
  },
  liveCampaignEditor: {
    budgetLifetime: 'lifetime',
    budgetDaily: 'daily',
    religiousInterestsEGChristianityBible: 'Religious interests (e.g. Christianity, Bible) are no longer offered by Meta: not available for ad targeting.',
    removeV: 'Remove {{v}}',
    metaAccountNotConnectedSearchUnavailable: 'Meta account not connected: search unavailable.',
    theAgeMustBeBetween13: 'The age must be between 13 and 65.',
    theMinimumAgeExceedsTheMaximum: 'The minimum age exceeds the maximum age.',
    theBudgetMustBeGreaterThan: 'The budget must be greater than 0.',
    theEndDateMustBeIn: 'The end date must be in the future.',
    campaignUpdatedOnThePlatform: 'Campaign updated on the platform.',
    adAccountNotConnectedEditNot: 'Ad account not connected: edit not possible.',
    theseChangesAreAppliedDirectlyTo: 'These changes are applied directly to the ad set on the platform. The campaign\'s status does not change (use Activate / Pause).',
    geographicAreas: 'Geographic areas',
    searchACountryARegionOr: 'Search a country, a region or a city…',
    searching: 'Searching…',
    minAge: 'Min age',
    maxAge: 'Max age',
    interestsMeta: 'Interests (Meta)',
    searchAnInterestEGMusic: 'Search an interest (e.g. Music, Family, Travel)…',
    audienceSizeUpperBoundPeople: ' · ~{{audience_size_upper_bound}} people',
    customAudiences: 'Custom audiences',
    lookalikeAudiences: 'Lookalike audiences',
    excludedAudiences: 'Excluded audiences',
    budget: 'Budget',
    theBudgetTypeDailyTotalCannot: 'The budget type (daily / total) cannot be changed.',
    endDate: 'End date',
    theStartDateOfACampaign: 'The start date of a campaign that has already started cannot be changed.',
    changeRefusedError: 'Change refused: {{error}}',
    saving: 'Saving…',
    saveOnThePlatform: 'Save on the platform',
    cancel: 'Cancel',
  },
  updatePublishedControls: {
    archiveTheCampaign: 'Archive the campaign?',
    theCampaignWillStopDeliveringAnd: 'The campaign will stop delivering and move to the archive. This action is permanent on the platform.',
    archive: 'Archive',
    campaignPaused: 'Campaign paused.',
    campaignReactivated: 'Campaign reactivated.',
    campaignArchived: 'Campaign archived.',
    editTheTextTheCreative: 'Edit the text / the creative',
    confirmTheChange: 'Confirm the change',
    editingTheCreativeResetsTheAd: 'Editing the creative resets the ad\'s learning phase and submits it to a new platform review (delivery may be suspended during the review). Continue?',
    editAndResetLearning: 'Edit and reset learning',
    adUpdatedSubmittedToANew: 'Ad updated — submitted to a new review.',
    adUpdated: 'Ad updated.',
    editingAMetaCreativeCreatesA: '⚠ Editing a Meta creative creates a new creative: learning is reset and the ad goes back into review.',
    title: 'Title',
    text: 'Text',
    link: 'Link',
    actionButton: 'Action button',
    mediaUrl: 'Media (URL)',
    saving: 'Saving…',
    save: 'Save',
    cancel: 'Cancel',
  },
};

export default en;
